import { Input } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { useClient } from '@/stores/useClient.ts'
import { useEffect, useRef, useState } from 'react'
import { useProfile } from '@/stores/useAuth.ts'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { HeyDriveType } from '@/models/drive.ts'
import { Trans, useTranslation } from 'react-i18next'
import { captureException } from '@/controller/sentryHelper.ts'
import DateModal from '@/components/DateModal.tsx'

export default function DemoPage() {
  const { t } = useTranslation()
  const { createDemo } = useClient()
  const [isCreatingDemo, setIsCreatingDemo] = useState(false)
  const [email, setEmail] = useState('')
  const { setAuthenticationToken, setProfile } = useProfile()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [loginLinkSent, setLoginLinkSent] = useState(false)
  const [eventDate, setEventDate] = useState<null | Date>()
  const [searchParams] = useSearchParams()
  const driveType: HeyDriveType | undefined = searchParams.get('type') as HeyDriveType || undefined
  const country: string | undefined = searchParams.get('c') || undefined
  const language: string | undefined = searchParams.get('l') || undefined
  const shallShowEventDate = !driveType || driveType == HeyDriveType.WEDDING || driveType == HeyDriveType.BIRTHDAY || driveType == HeyDriveType.PARTY
  const dateModalRef = useRef<HTMLDialogElement>(null);
  console.log('REFFFF', dateModalRef)

  useEffect(() => {
    // Trigger Cookiebot popup for demo users
    if (window.Cookiebot) {
      window.Cookiebot.show()
    }
  }, [])

  async function onSubmit(e: any) {
    e.preventDefault()
    e.stopPropagation()

    if (isCreatingDemo || loginLinkSent) {
      return
    }

    if (shallShowEventDate && !eventDate) {
      setError(t('demo.pleaseEnterDate'))
      return
    }

    setIsCreatingDemo(true)

    try {
      const res = await createDemo(email, driveType, country, language, eventDate ? toDjangoDateString(eventDate) : undefined)

      // Email already belongs to an account: the backend sends a new login link
      // by email instead of creating a demo. Lock the form and inform the user.
      if (res.data?.login_link_sent) {
        setError(null)
        setLoginLinkSent(true)
        return
      }

      setProfile(res.data.profile)
      const haToken = res.headers['ha-token']
      setAuthenticationToken(haToken)

      const driveId = res.data.drive
      navigate(`/d/${driveId}?_ha=${haToken}`)
      try {
        console.log('Demo signup successful')
      } catch (error) {
        captureException(error, 'Tracking Demo user')
      }
    } catch (e: any) {
      console.error('Demo Error', e)
      if (e.response) {
        if (e.response.status === 400) {
          if (e.response.data && e.response.data.includes('invalid_data') && e.response.data.includes('email')) {
            setError(t('demo.invalidEmail'))
          }
          console.log('data', e.response.data)
        }

      }
      // alert('Error creating demo: ' + JSON.stringify(e.response))
    } finally {
      setIsCreatingDemo(false)
    }
  }

  // @ts-ignore
  const driveTypeImage = {
    [HeyDriveType.BIRTHDAY]: '/demoX/app-screenshot-geburtstag.jpeg',
    [HeyDriveType.PARTY]: '/demoX/app-screenshot-party.jpeg',
    [HeyDriveType.COMPANY]: '/demoX/app-screenshot-firmenfeier.jpeg',
  }[driveType] || '/demoX/app-screenshot-hochzeit.jpeg'

  const eventDateName = !driveType || driveType == HeyDriveType.WEDDING ? t('demo.weddingDate') : (driveType == HeyDriveType.BIRTHDAY ? t('demo.birthdayDate') : t('demo.eventDate'))

  return <div className='bg-cover bg-top h-full' style={{ backgroundImage: `url(${driveTypeImage})` }}>
    <div className="w-full h-full bg-[black] bg-opacity-40">
      <div className='w-full h-full flex items-center justify-center'>
        <div className='bg-neutral p-4 m-3 rounded-xl text-center flex flex-col gap-4 shadow'>
          <h1 className='text-xl font-semibold'>{t('demo.freeDemo')}</h1>
          <p>{t('demo.getInstantAccess')}</p>
          <form onSubmit={onSubmit} className='flex flex-col gap-5'>
            <Input placeholder={t('common.yourEmail')} required value={email} onChange={e => setEmail(e.target.value)} disabled={loginLinkSent} />
            {
              shallShowEventDate && <>
                <label className='form-control'>
                  <div className='label'>
                    <span className="label-text">{eventDateName} {t('demo.dateExample', {date: '21.07.2026'})}</span>
                  </div>
                  <Input type='text' placeholder='dd.mm.yyyy' required disabled={loginLinkSent}
                         value={eventDate ? eventDate.toLocaleDateString() : ''} onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    dateModalRef.current!.showModal()
                  }}
                         onInput={(e) => {
                           console.log(e)
                           e.preventDefault()
                           e.stopPropagation()
                           dateModalRef.current!.showModal()
                         }}
                  />
                </label>
              </>
            }
            { error && <p className='text-error text-sm'>{ error }</p> }
            {loginLinkSent && (
              <div className='rounded-lg bg-success bg-opacity-10 border border-success px-4 py-3 text-success text-sm text-left'>
                {t('demo.loginLinkSent')}
              </div>
            )}
            <div>
              <Button color='primary' className='' disabled={loginLinkSent}>
                {t('demo.accessDemo')}
              </Button>
            </div>
          </form>
          <p className='text-sm opacity-50 max-w-[20rem]'>
            <Trans i18nKey='activate.registrationAgreement'>
              <a className='underline' href="https://fridaysnap.de/datenschutzerklaerung/" />
            </Trans>
          </p>
        </div>
        {/*<div className="container mx-auto bg-primary">*/}
        {/*  snrtnrt*/}
        {/*</div>*/}
      </div>
    </div>
    <DateModal
      modalRef={dateModalRef}
      date={eventDate}
      onSetDate={date => date ? setEventDate(date) : null}
    />
  </div>
}

function toDjangoDateString(dateObj: Date): string {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0"); // months are 0-based
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
